// ============================================================================
// ENTRY POINT - Wrapped in IIFE for silent early return on duplicate execution
// ============================================================================
import React from "react";
import { debugLogger } from "./utils/debugLogger";

// IIFE permite early return silencioso ao invés de throw Error
(async function bootstrap() {
  // Guard: Prevenir execução duplicada SEM lançar erro
  // MAS permitir re-execução se #root está vazio (BFCache recovery)
  if ((window as any).__MAIN_ENTRY_EXECUTED__) {
    const root = document.getElementById('root');
    const hasContent = root && root.children.length > 0;
    
    // Se já executou E o #root tem conteúdo, é duplicação real → return
    if (hasContent) {
      debugLogger.debug("init", "Entry point already executed with content, returning silently");
      return;
    }
    
    // Se já executou MAS o #root está vazio, é BFCache → resetar e continuar
    debugLogger.warn("init", "BFCache detected: entry executed but #root is empty, re-initializing...");
    delete (window as any).__MAIN_ENTRY_EXECUTED__;
    delete (window as any).__REACT_ROOT_RENDERED__;
    delete (window as any).__TELEMETRY_INITIALIZED__;
  }

  // Marcar como executado ANTES de tudo
  (window as any).__MAIN_ENTRY_EXECUTED__ = true;
  debugLogger.debug("init", "Entry point executing for the first time...");

  // Imports dinâmicos dentro da IIFE
  const ReactDOM = await import("react-dom/client");
  const { default: App } = await import("./App");
  const { default: AppProviders } = await import("./providers/AppProviders");
  await import("./index.css");
  const { datadogRum } = await import("@datadog/browser-rum");
  const { reactPlugin } = await import("@datadog/browser-rum-react");
  const { datadogLogs } = await import("@datadog/browser-logs");

  /**
   * Fetch application version from app_settings using singleton cache
   * Returns commit SHA or 'local-dev' as fallback
   * 
   * CRITICAL: Implements 3-second timeout to prevent infinite loading
   * if appSettingsCache.getSettings() hangs (network issue, DB down, etc.)
   */
  const fetchAppVersion = async (): Promise<string> => {
    const FETCH_TIMEOUT_MS = 3000; // 3 seconds max
    
    try {
      // Create timeout promise to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('App version fetch timeout')), FETCH_TIMEOUT_MS)
      );
      
      const { appSettingsCache } = await import("./lib/appSettingsCache");
      
      // Race between actual fetch and timeout
      const settings = await Promise.race([
        appSettingsCache.getSettings(),
        timeoutPromise
      ]);
      
      return settings.last_commit_sha;
    } catch (error) {
      // Log appropriately based on error type
      if (error instanceof Error && error.message.includes('timeout')) {
        debugLogger.warn("init", "App version fetch timed out after 3s, using fallback");
      } else {
        debugLogger.error("init", "Failed to fetch app version:", error);
      }
      debugLogger.warn("init", "Using fallback version: local-dev");
      return "local-dev";
    }
  };

  type AppEnvironment = "production" | "staging" | "development" | "preview" | "local" | "unknown";

  // Calculate environment dynamically based on hostname, prioritizing production
  const getEnvironment = (): AppEnvironment => {
    const hostname = window.location.hostname;

    // Production environments (highest priority)
    if (hostname === "interlaced.app" || hostname === "app.interlaced.app") {
      return "production";
    }

    // Staging environment (pre-prod gate)
    if (hostname === "staging.interlaced.app") {
      return "staging";
    }

    // Dedicated DEV environment (branch develop)
    if (hostname === "dev.interlaced.app") {
      return "development";
    }

    // Preview environments with ID prefix
    if (hostname.includes("preview")) {
      return "preview";
    }

    // Development environment (Lovable editor interface)
    if (hostname.includes(".lovableproject.com") || hostname.includes("sandbox.lovable.dev")) {
      return "development";
    }

    // Local development
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "local";
    }

    // Default fallback
    return "unknown";
  };

  // Helper to get service name based on environment
  const getServiceName = (env: AppEnvironment): string => {
    return env === "production" ? "interlaced-app" : "interlaced-app-debug";
  };

  /**
   * Initialize Datadog RUM and Logs
   */
  const initializeTelemetry = (environment: AppEnvironment, appVersion: string) => {
    // GUARD: só pula se o RUM estiver realmente funcional
    const isRumWorking = !!datadogRum.getInternalContext?.();
    if ((window as any).__TELEMETRY_INITIALIZED__ && isRumWorking) {
      debugLogger.debug("telemetry", "Already initialized and RUM working, skipping...");
      return;
    }
    // Se o guard está setado mas o RUM não está funcional, re-inicializa
    if ((window as any).__TELEMETRY_INITIALIZED__ && !isRumWorking) {
      debugLogger.warn("telemetry", "Guard set but RUM is not working, re-initializing...");
      delete (window as any).__TELEMETRY_INITIALIZED__;
    }

    debugLogger.debug("telemetry", `Initializing with version: ${appVersion}, env: ${environment}`);

    // 1. Initialize Datadog Logs (apenas se ainda não foi inicializado)
    if (!(window as any).DD_LOGS) {
      datadogLogs.init({
        clientToken: "pub352be0094e46ee9ccdc025eedcbf950e",
        site: "datadoghq.com",
        service: getServiceName(environment),
        env: environment,
        forwardErrorsToLogs: true,
        allowUntrustedEvents: true,
        sessionSampleRate: 100,
        useSecureSessionCookie: true,
        version: appVersion,
        storeContextsAcrossPages: true,
        usePartitionedCrossSiteSessionCookie: true,
      });
    }

    // 2. Initialize Datadog RUM (somente se ainda não estiver realmente inicializado)
    if (!datadogRum.getInternalContext?.()) {
      datadogRum.init({
        applicationId: "934b7197-abeb-43e3-be80-d0b71b4eb9ad",
        clientToken: "pub352be0094e46ee9ccdc025eedcbf950e",
        site: "datadoghq.com",
        service: getServiceName(environment),
        env: environment,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        defaultPrivacyLevel: "allow",
        useSecureSessionCookie: true,
        usePartitionedCrossSiteSessionCookie: true,
        storeContextsAcrossPages: true,
        allowFallbackToLocalStorage: true,
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        version: appVersion,
        // Distributed tracing: propaga trace IDs para conectar RUM com backend traces
        allowedTracingUrls: [window.location.origin],

        // Normalizar stack traces removendo query params de cache busting
        // + Prevenir overflow de Datadog com payloads grandes (imagens base64)
        beforeSend: (event) => {
          // Normalize stack traces
          if (
            event.error &&
            typeof event.error === "object" &&
            "stack" in event.error &&
            typeof event.error.stack === "string"
          ) {
            event.error.stack = event.error.stack.replace(/\?[vt]=\d+/g, "");
          }

          // Detect and truncate large payloads (>200KB)
          const eventString = JSON.stringify(event);
          if (eventString.length > 200000) {
            debugLogger.warn("[Datadog] Event payload too large, truncating context", {
              originalSize: eventString.length,
              eventType: event.type,
            });

            // Remove context to reduce size
            if (event.context) {
              event.context = { truncated: true, originalSize: eventString.length };
            }
          }

          // Filter out data URLs from event context
          if (event.context && typeof event.context === "object") {
            const cleanContext = (obj: any): any => {
              if (!obj || typeof obj !== "object") return obj;

              const cleaned: any = {};
              for (const key in obj) {
                if (typeof obj[key] === "string" && obj[key].startsWith("data:image/")) {
                  cleaned[key] = "[data URL truncated]";
                } else if (typeof obj[key] === "object" && obj[key] !== null) {
                  cleaned[key] = cleanContext(obj[key]);
                } else {
                  cleaned[key] = obj[key];
                }
              }
              return cleaned;
            };

            event.context = cleanContext(event.context);
          }

          return true;
        },

        plugins: [reactPlugin({ router: false })] as any,
      });

      // Confirmação assíncrona e fallback de view
      datadogRum.onReady(() => {
        try {
          const ctx = datadogRum.getInternalContext?.();
          debugLogger.debug("rum", "onReady context:", ctx);
          if (!ctx) {
            debugLogger.warn("rum", "No RUM context after init; starting view manually...");
            datadogRum.startView();
            setTimeout(() => {
              debugLogger.debug("rum", "Context after manual startView:", datadogRum.getInternalContext?.());
            }, 1000);
          }
        } catch (e) {
          debugLogger.warn("rum", "onReady check failed", e);
        }
      });
    }

    // Marcar como inicializado globalmente
    (window as any).__TELEMETRY_INITIALIZED__ = true;
    debugLogger.debug("telemetry", "Datadog RUM and Logs initialized successfully");
  };

  const environment = getEnvironment();

  // Fetch version and initialize telemetry asynchronously
  fetchAppVersion()
    .then((appVersion) => {
      // Initialize Datadog RUM and Logs
      initializeTelemetry(environment, appVersion);

      // Set user data in RUM/Logs if available
      const userData = (() => {
        try {
          const raw = Object.values(localStorage).find((v) => typeof v === "string" && v.includes('"user":'));
          if (!raw || typeof raw !== "string") return null;
          const parsed = JSON.parse(raw);
          const user = (parsed as any).user;

          let username = user?.user_metadata?.username;
          if (!username && user?.email) {
            username = (user.email as string).split("@")[0];
          }

          return {
            id: user?.id,
            email: user?.email,
            name: username || "user",
          };
        } catch {
          return null;
        }
      })();

      if (userData && userData.email) {
        datadogRum.setUser(userData);
        datadogLogs.setUser(userData);
      }
    })
    .catch((error) => {
      console.error("[Main] CRITICAL: Telemetry initialization failed:", error);
      // Emergency fallback if fetch completely fails
      initializeTelemetry(environment, "local-dev");
    });

  // Add custom animations for our components
  const style = document.createElement("style");
  style.textContent = `
    @keyframes scale-in {
      0% { transform: scale(0.95); opacity: 0; }
      100% { transform: scale(1); opacity: 1; }
    }
    .animate-scale-in {
      animation: scale-in 0.2s ease-out forwards;
    }
    
    /* Hide scrollbar for the tabs but keep them scrollable */
    .no-scrollbar::-webkit-scrollbar {
      display: none;
    }
    .no-scrollbar {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    
    /* Luxury button style */
    .luxury-button {
      background: linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%);
      color: white;
      position: relative;
      overflow: hidden;
      transition: all 0.3s ease;
    }
    
    .luxury-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(138, 45, 226, 0.3);
    }
    
    .luxury-button:active {
      transform: translateY(0);
    }
    
    .luxury-button:before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.2),
        transparent
      );
      transition: all 0.6s;
    }
    
    .luxury-button:hover:before {
      left: 100%;
    }
  `;
  document.head.appendChild(style);

  /**
   * Verificar se React hooks estão disponíveis
   * Previne erro: "Cannot read properties of null (reading 'useEffect')"
   */
  const isReactReady = (): boolean => {
    try {
      return React.useEffect !== undefined && React.useEffect !== null && typeof React.useEffect === "function";
    } catch {
      return false;
    }
  };

  /**
   * Retry mechanism com backoff exponencial
   * Aguarda React estar pronto antes de renderizar
   */
  const renderWithRetry = (maxAttempts = 3, baseDelay = 50) => {
    let attempts = 0;

    const tryRender = () => {
      if (!isReactReady()) {
        if (attempts < maxAttempts) {
          attempts++;
          const delay = baseDelay * attempts; // backoff exponencial
          debugLogger.warn("react", `React not ready, retry ${attempts}/${maxAttempts} in ${delay}ms...`);
          setTimeout(tryRender, delay);
          return;
        }

        debugLogger.error("react", "React failed to initialize after max attempts");
        // Fallback: tentar renderizar mesmo assim
        console.error("CRITICAL: React hooks not available after retries");
      }

      // GUARD: Prevenir múltiplas renderizações do React
      // MAS permitir re-render se #root está vazio (BFCache recovery)
      const rootElement = document.getElementById("root") as HTMLElement;
      const rootHasContent = rootElement && rootElement.children.length > 0;
      
      if (!(window as any).__REACT_ROOT_RENDERED__ || !rootHasContent) {
        // Reset guard if re-rendering due to BFCache
        if ((window as any).__REACT_ROOT_RENDERED__ && !rootHasContent) {
          debugLogger.warn("react", "Guard set but root is empty (BFCache), re-rendering...");
          delete (window as any).__REACT_ROOT_RENDERED__;
        }
        
        ReactDOM.createRoot(rootElement).render(
          <AppProviders>
            <App />
          </AppProviders>,
        );

        (window as any).__REACT_ROOT_RENDERED__ = true;
        debugLogger.debug("react", "Root rendered successfully");
      } else {
        debugLogger.debug("react", "Root already rendered with content, skipping...");
      }
    };

    tryRender();
  };

  // Executar renderização com retry
  renderWithRetry();
})();

// ✅ HMR: Reset guards on hot reload (fora da IIFE para funcionar corretamente)
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    delete (window as any).__MAIN_ENTRY_EXECUTED__;
    delete (window as any).__REACT_ROOT_RENDERED__;
    delete (window as any).__TELEMETRY_INITIALIZED__;
    debugLogger.debug("hmr", "Main entry guards cleared for hot reload");
  });
}
